<template>
  <!-- Overlay to block iframe interactions during drag -->
  <div v-if="isDragging" class="drag-overlay"></div>

  <!-- Dragging box -->
  <DragBox
    v-if="isDragging"
    :position="{ x: dragBox.x, y: dragBox.y }"
    :size="{ width: dragBox.width, height: dragBox.height }"
  />

  <!-- Actual window -->
  <div
    class="window"
    :class="{ hidden: loading, active: isActive }"
    :style="{
      top: app.y + 'px',
      left: app.x + 'px',
      width: width + 'px',
      height: height + 'px',
      position: 'absolute',
      zIndex: zIndex
    }"
    @mousedown="bringToFront"
  >
    <div
      class="title-bar"
      :class="{ inactive: !isActive }"
      @mousedown.stop.prevent="startDrag"
    >
      <div class="title-bar-text">
        <img :src="app.icon" alt="icon" class="title-bar-icon" />
        {{ app.label }}
      </div>
      <div class="title-bar-controls">
        <button aria-label="Minimize" @click="onMinimize(app.id)"></button>
        <button aria-label="Close" @click="onClose(app.id)"></button>
      </div>
    </div>
    <div class="window-body" :style="{ width: '100%', height: 'calc(100% - 30px)', overflow: 'hidden', margin: app.margin + 'px' }">
      <iframe
        ref="iframe"
        v-if="app.content?.iframe"
        :src="app.content.iframe"
        :style="{ width: '100%', height: '100%', border: 'none' }"
        @load="onIframeLoad"
      ></iframe>
      <div v-else-if="app.content?.html" v-html="app.content.html" style="width: 100%; height: 100%; overflow: auto;"></div>
      <slot v-else style="width: 100%; height: 100%; display: block;" />
    </div>

    <!-- Resize handles -->
    <div class="resize-handle top" @mousedown.stop.prevent="(e) => startResize('top', e)" />
    <div class="resize-handle right" @mousedown.stop.prevent="(e) => startResize('right', e)" />
    <div class="resize-handle bottom" @mousedown.stop.prevent="(e) => startResize('bottom', e)" />
    <div class="resize-handle left" @mousedown.stop.prevent="(e) => startResize('left', e)" />
    <div class="resize-handle top-left" @mousedown.stop.prevent="(e) => startResize('top-left', e)" />
    <div class="resize-handle top-right" @mousedown.stop.prevent="(e) => startResize('top-right', e)" />
    <div class="resize-handle bottom-left" @mousedown.stop.prevent="(e) => startResize('bottom-left', e)" />
    <div class="resize-handle bottom-right" @mousedown.stop.prevent="(e) => startResize('bottom-right', e)" />
  </div>
</template>

<script setup>
import DragBox from './DragBox.vue';
import { ref, onMounted, useTemplateRef } from 'vue';

const props = defineProps({
  app: Object,
  onClose: Function,
  onMinimize: Function,
});

const iframeRef = useTemplateRef('iframe')
const isActive = ref(true);
const width = ref(props.app.width); // Initialize with app.width
const height = ref(props.app.height); // Initialize with app.height
const zIndex = ref(0);
const loading = ref(!!props.app.content?.iframe); // Set loading to true if iframe is present
const progress = ref(0); // Percentuale di progresso
const isDragging = ref(false); // Track if dragging is active
const dragBox = ref({ x: 0, y: 0, width: 0, height: 0 }); // Dragging box dimensions

function bringToFront() {
  zIndex.value = getNextZIndex();
  isActive.value = true;
}

function getNextZIndex() {
  if (!window._zCounter) window._zCounter = 1000;
  return ++window._zCounter;
}

function onIframeLoad() {
  console.log('Iframe loaded');
  loading.value = false;
  progress.value = 100;
}

onMounted(() => {
  bringToFront();

  if (loading.value) {
    const interval = setInterval(() => {
      if (progress.value < 90) {
        progress.value += 10;
        console.log('Progress:', progress.value);
      } else {
        clearInterval(interval);
      }
    }, 50);
  }

  document.addEventListener('mousedown', (e) => {
    const windowElement = e.target.closest('.window');
    if (!windowElement || windowElement !== e.currentTarget) {
      isActive.value = false;
    }
  });

  // Add event listener for iframes
  const iframe = iframeRef.value;
  if (iframe && iframe.contentWindow) {
    iframe.contentWindow.addEventListener('mousedown', () => {
      isActive.value = true;
    });
  }
});

defineExpose({
  bringToFront
});

function startDrag(e) {
  if (e.target.closest('.title-bar-controls')) {
    return; // Prevent dragging when clicking on title bar buttons
  }

  bringToFront();

  // Initialize the drag box
  dragBox.value = {
    x: props.app.x,
    y: props.app.y,
    width: width.value,
    height: height.value,
  };
  isDragging.value = true;

  const offsetX = e.clientX - props.app.x;
  const offsetY = e.clientY - props.app.y;

  const onMouseMove = (e) => {
    dragBox.value.x = e.clientX - offsetX;
    dragBox.value.y = e.clientY - offsetY;
  };

  const onMouseUp = () => {
    // Apply the final position to the real window
    props.app.x = dragBox.value.x;
    props.app.y = dragBox.value.y;

    isDragging.value = false;

    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
  };

  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('mouseup', onMouseUp);
}

function startResize(direction, e) {
  bringToFront();
  const startX = e.clientX;
  const startY = e.clientY;
  const startWidth = width.value;
  const startHeight = height.value;
  const startLeft = props.app.x;
  const startTop = props.app.y;

  const onMouseMove = (e) => {
    if (direction.includes('right')) {
      width.value = Math.max(150, startWidth + (e.clientX - startX));
    }
    if (direction.includes('bottom')) {
      height.value = Math.max(100, startHeight + (e.clientY - startY));
    }
    if (direction.includes('left')) {
      const deltaX = e.clientX - startX;
      width.value = Math.max(150, startWidth - deltaX);
      props.app.x = startLeft + deltaX;
    }
    if (direction.includes('top')) {
      const deltaY = e.clientY - startY;
      height.value = Math.max(100, startHeight - deltaY);
      props.app.y = startTop + deltaY;
    }
  };

  const onMouseUp = () => {
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
  };

  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('mouseup', onMouseUp);
}
</script>

<style scoped>
.window {
  min-width: 200px;
  min-height: 150px;
  resize: none;
  box-sizing: border-box;
}

.title-bar-text {
  display: flex;
  align-items: center;
  gap: 4px;
}

.title-bar-icon {
  width: 16px;
  height: 16px;
  image-rendering: pixelated;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
}

.loading-text {
  font-family: 'Pixelated MS Sans Serif', sans-serif;
  font-size: 14px;
  color: black;
}


.resize-handle {
  position: absolute;
  background: transparent;
}

.resize-handle.top {
  top: -5px;
  left: 0;
  right: 0;
  height: 10px;
  cursor: ns-resize;
}

.resize-handle.right {
  top: 0;
  right: -5px;
  bottom: 0;
  width: 10px;
  cursor: ew-resize;
}

.resize-handle.bottom {
  bottom: -5px;
  left: 0;
  right: 0;
  height: 10px;
  cursor: ns-resize;
}

.resize-handle.left {
  top: 0;
  left: -5px;
  bottom: 0;
  width: 10px;
  cursor: ew-resize;
}

.resize-handle.top-left {
  top: -5px;
  left: -5px;
  width: 10px;
  height: 10px;
  cursor: nwse-resize;
}

.resize-handle.top-right {
  top: -5px;
  right: -5px;
  width: 10px;
  height: 10px;
  cursor: nesw-resize;
}

.resize-handle.bottom-left {
  bottom: -5px;
  left: -5px;
  width: 10px;
  height: 10px;
  cursor: nesw-resize;
}

.resize-handle.bottom-right {
  bottom: -5px;
  right: -5px;
  width: 10px;
  height: 10px;
  cursor: nwse-resize;
}

.drag-box {
  z-index: 999999;
  position: absolute;
  width: 300px;
  height: 200px;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='100%25' height='100%25'%3e%3cdefs%3e%3cpattern id='checker' patternUnits='userSpaceOnUse' width='6' height='6'%3e%3crect x='0' y='0' width='3' height='3' fill='%23555555'/%3e%3crect x='3' y='3' width='3' height='3' fill='%23555555'/%3e%3c/pattern%3e%3c/defs%3e%3crect x='0' y='0' width='100%25' height='100%25' fill='none' stroke='url(%23checker)' stroke-width='4' /%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-size: 100% 100%;
}

.drag-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: transparent;
  z-index: 9998; /* Deve essere sotto il drag-box ma sopra l'iframe */
  pointer-events: all;
}
</style>
